import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Dashboard, useDashboardFeatureFlags } from '@components'
import {
  BackButton,
  Button,
  HeadContent,
  Icon,
  Skeleton,
  ToolbarButton,
  useMediaQueryContext,
} from '@fourthwall/components'
import { DownloadIcon, FilterIcon } from '@fourthwall/icons'
import { isWebView } from '@fourthwall/utils'
import * as EmptyState from '@modules/Dashboard/components/EmptyState/EmptyState'
import type { LocationState } from '@types'
import { routing } from '@utils/routing'

import { AnalyticsCard } from '../../analyticsModule/components/AnalyticsCard/AnalyticsCard'
import { AnalyticsFiltersSelect } from '../../analyticsModule/components/AnalyticsFilterSelect/AnalyticsFiltersSelect'
import { AnalyticsFiltersButton } from '../../analyticsModule/components/AnalyticsFiltersButton/AnalyticsFiltersButton'
import { AnalyticsReportLayout } from '../../analyticsModule/components/AnalyticsReportLayout/AnalyticsReportLayout'
import { AnalyticsTable } from '../../analyticsModule/components/AnalyticsTable/AnalyticsTable'
import { AnalyticsToolbar } from '../../analyticsModule/components/AnalyticsToolbar/AnalyticsToolbar'
import { AnalyticsCompareDateRangeSelect } from '../../analyticsModule/components/tools/AnalyticsCompareDateRangeSelect/AnalyticsCompareDateRangeSelect'
import { AnalyticsDateRangeSelect } from '../../analyticsModule/components/tools/AnalyticsDateRangeSelect/AnalyticsDateRangeSelect'
import { AnalyticsPrecisionSelect } from '../../analyticsModule/components/tools/AnalyticsPrecisionSelect/AnalyticsPrecisionSelect'
import { analyticsDecoratorLabelsMap } from '../../consts'
import { GoogleAnalyticsReportBottomContent } from '../../modules/GoogleAnalytics/components/GoogleAnalyticsReportBottomContent/GoogleAnalyticsReportBottomContent'
import * as S from './AnalyticsReport.styled'
import { useAnalyticsReport } from './hooks/useAnalyticsReport'

export const AnalyticsReport = () => {
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const location = useLocation()
  const locationState = location.state as LocationState
  const media = useMediaQueryContext()
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false)
  const toggleFiltersPanel = () => setIsFiltersPanelOpen((state) => !state)
  const {
    enabled,
    externalSource,
    decorator,
    isLoading,
    title,
    table,
    card,
    filters,
    hasActiveFilters,
    supportsPrecisions,
    params,
    isExporting,
    onExport,
  } = useAnalyticsReport()

  const showFilters = !!filters.filters.length
  const showGoogleAnalyticsBottomContent = externalSource === 'googleAnalytics'

  const backButtonProps = (() => {
    if (locationState?.from) {
      return {
        label: locationState.from.label,
        to: {
          pathname: locationState.from.location.pathname,
          search: locationState.from.location.search,
        },
      }
    }

    return {
      label: 'Reports',
      to: routing.analyticsV2.reports.self,
    }
  })()

  if (!enabled) {
    return (
      <>
        <HeadContent title="Unknown report" titleTemplate={isWebView() ? title : undefined} />
        <S.PageLayout size="full">
          <Dashboard.Section topContent={<BackButton {...backButtonProps} />}>
            <EmptyState.Root
              title="Report not found"
              subtitle="We do not handle this report yet. Please try another one."
            />
          </Dashboard.Section>
        </S.PageLayout>
      </>
    )
  }

  if (isLoading) {
    return (
      <>
        <HeadContent title={title} titleTemplate={isWebView() ? title : undefined} />
        <S.PageLayout size="full">
          <Dashboard.Section topContent={<BackButton {...backButtonProps} />}>
            <S.HeaderSkeleton>
              <Skeleton.Shape height={media.tablet ? 36 : 28} width={250} />
              <S.ToolbarContent>
                {media.tablet ? (
                  <>
                    <Skeleton.Shape height={40} width={100} />
                    <Skeleton.Shape height={40} width={100} />
                  </>
                ) : (
                  <>
                    <Skeleton.Shape height={40} width={40} />
                    <Skeleton.Shape height={40} width={40} />
                  </>
                )}
              </S.ToolbarContent>
            </S.HeaderSkeleton>
            <AnalyticsReportLayout
              toolbar={
                <AnalyticsToolbar
                  actions={
                    <S.Actions>
                      <Skeleton.Shape height={40} width={100} />
                      <Skeleton.Shape height={40} width={100} />
                    </S.Actions>
                  }
                >
                  <Skeleton.Shape height={38} width={200} />
                  <Skeleton.Shape height={38} width={200} />
                  <Skeleton.Shape height={38} width={200} />
                </AnalyticsToolbar>
              }
            >
              <>
                <Skeleton.Shape height={430} />
                <Skeleton.Shape height={300} />
              </>
            </AnalyticsReportLayout>
          </Dashboard.Section>
        </S.PageLayout>
      </>
    )
  }

  return (
    <>
      <HeadContent title={title} titleTemplate={isWebView() ? title : undefined} />
      <S.PageLayout size="full">
        <Dashboard.Section
          name={isWebView() ? undefined : title}
          topContent={<BackButton {...backButtonProps} />}
          bottomContent={
            showGoogleAnalyticsBottomContent || decorator ? (
              <S.BottomContent>
                {decorator && (
                  <S.DecoratorLabel>{analyticsDecoratorLabelsMap[decorator]}</S.DecoratorLabel>
                )}
                {showGoogleAnalyticsBottomContent && <GoogleAnalyticsReportBottomContent />}
              </S.BottomContent>
            ) : undefined
          }
          toolbarContent={
            <S.ToolbarContent>
              {media.tablet ? (
                <>
                  <Button
                    leftIcon={<Icon component={DownloadIcon} height={18} />}
                    label="Export"
                    appearance="secondary"
                    size="small"
                    loading={isExporting}
                    onClick={onExport}
                  />
                  {showFilters && (
                    <AnalyticsFiltersButton
                      isActive={hasActiveFilters}
                      onClick={toggleFiltersPanel}
                    />
                  )}
                </>
              ) : (
                <>
                  <ToolbarButton icon={DownloadIcon} isLoading={isExporting} onClick={onExport} />
                  {showFilters && (
                    <ToolbarButton
                      icon={FilterIcon}
                      onClick={toggleFiltersPanel}
                      active={hasActiveFilters}
                      activityIndicator
                    />
                  )}
                </>
              )}
            </S.ToolbarContent>
          }
        >
          <AnalyticsReportLayout
            toolbar={
              <AnalyticsToolbar
                actions={
                  <S.Actions>
                    <Button
                      leftIcon={<Icon component={DownloadIcon} height={18} />}
                      label="Export"
                      appearance="secondary"
                      size="small"
                      loading={isExporting}
                      onClick={onExport}
                    />
                    {showFilters && (
                      <AnalyticsFiltersButton
                        isActive={hasActiveFilters}
                        onClick={toggleFiltersPanel}
                      />
                    )}
                  </S.Actions>
                }
              >
                <AnalyticsDateRangeSelect {...params.dateRange} />
                {dashboardFeatureFlags.analyticsV2Compare.enabled && (
                  <AnalyticsCompareDateRangeSelect {...params.compareDateRange} />
                )}
                {supportsPrecisions && <AnalyticsPrecisionSelect {...params.precision} />}
              </AnalyticsToolbar>
            }
            sidebar={filters.filters ? <AnalyticsFiltersSelect {...filters} /> : undefined}
            isSidebarOpen={isFiltersPanelOpen}
            hideSidebar={toggleFiltersPanel}
          >
            <>
              {card && <AnalyticsCard {...card} />}

              {table && (
                <S.AnalyticsTableWrapper>
                  <AnalyticsTable {...table} />
                </S.AnalyticsTableWrapper>
              )}
            </>
          </AnalyticsReportLayout>
        </Dashboard.Section>
      </S.PageLayout>
    </>
  )
}
